import { useState } from 'react';
import { useAuthenticatedFetch } from '../App';

// Get shop domain from URL
const getShopDomain = () => new URLSearchParams(window.location.search).get('shop') || '';

export default function SimpleOnboardingPage() {
  const shopDomain = getShopDomain();
  const authenticatedFetch = useAuthenticatedFetch();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [accountMode, setAccountMode] = useState<'create' | 'connect'>('create');

  // Step completion state for this visit only. Never stored in the browser:
  // whether the shop is connected comes from our database (App.tsx).
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState<string>('');

  // Loading states
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Error/success states
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const markStepComplete = (stepId: string) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };

  const isStepComplete = (stepId: string) => completedSteps.includes(stepId);

  const validateForm = (): boolean => {
    setError('');

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }

    if (accountMode === 'create' && password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsCreatingAccount(true);
    setError('');

    try {
      const endpoint = accountMode === 'create'
        ? '/api/commerce-studio/create-account'
        : '/api/commerce-studio/connect-account';

      const response = await authenticatedFetch(`${endpoint}?shop=${shopDomain}`, {
        method: 'POST',
        headers: {
          'X-Shop-Domain': shopDomain
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        markStepComplete('create-account');
        setUserEmail(email);
        setShowAccountForm(false);
        setSuccessMessage(accountMode === 'create'
          ? 'Account created successfully!'
          : 'Account connected successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.error || 'Failed to process account. Please try again.');
      }
    } catch (err) {
      console.error('Account error:', err);
      setError('Connection error. Please check your internet and try again.');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleSyncProducts = async () => {
    setIsSyncing(true);
    setError('');

    try {
      // Call the Shopify connector's local sync endpoint (uses Shopify Admin API directly)
      const response = await authenticatedFetch(`/api/sync/products?shop=${shopDomain}`, {
        method: 'POST',
        headers: {
          'X-Shop-Domain': shopDomain
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        markStepComplete('sync-products');
        setSuccessMessage(`Product sync started! Sync ID: ${data.syncId || 'N/A'}`);
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError(data.error || data.details || 'Failed to sync products');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError('Failed to sync products. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleGoToDashboard = async () => {
    // Fetch dashboard URL from embed server (region-aware, set via COMMERCE_STUDIO_DASHBOARD env)
    try {
      const resp = await authenticatedFetch('/api/commerce-studio/dashboard-url');
      const data = await resp.json();
      if (data.url) {
        window.open(`${data.url}?shop=${shopDomain}&source=shopify`, '_blank');
        return;
      }
    } catch (e) {
      // fallback below
    }
    // Fallback: production dashboard
    window.open(`https://commerce.varai.ai?shop=${shopDomain}&source=shopify`, '_blank');
  };

  // App installation + activation is intentionally NOT a step here.
  // Per the single-source-of-truth principle (CS dashboard is the only
  // surface where merchants pick and configure apps), the Shopify
  // embedded wizard only covers account + sync, then hands off to
  // commerce.varai.ai for everything else.
  const allStepsComplete = ['create-account', 'sync-products'].every(step =>
    isStepComplete(step)
  );


  // Styles
  const styles = {
    container: { padding: '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', maxWidth: '900px', margin: '0 auto' },
    header: { textAlign: 'center' as const, marginBottom: '32px' },
    title: { fontSize: '28px', fontWeight: 600, color: '#1a1a2e', marginBottom: '8px' },
    subtitle: { fontSize: '16px', color: '#666', marginBottom: '16px' },
    shopBadge: { display: 'inline-block', padding: '4px 12px', backgroundColor: '#e8f4fd', color: '#0066cc', borderRadius: '16px', fontSize: '14px' },
    successBanner: { padding: '16px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '8px', marginBottom: '24px', textAlign: 'center' as const },
    errorBanner: { padding: '12px 16px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '8px', marginBottom: '16px', color: '#721c24' },
    successToast: { padding: '12px 16px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '8px', marginBottom: '16px', color: '#155724' },
    stepsContainer: { display: 'flex', flexDirection: 'column' as const, gap: '12px', marginBottom: '24px' },
    stepRow: (isComplete: boolean) => ({
      display: 'flex',
      alignItems: 'center',
      padding: '16px 20px',
      border: isComplete ? '2px solid #28a745' : '1px solid #e0e0e0',
      borderRadius: '12px',
      backgroundColor: isComplete ? '#f8fff9' : 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      gap: '16px'
    }),
    stepIndicator: (isComplete: boolean) => ({
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: isComplete ? '#28a745' : '#e9ecef',
      color: isComplete ? 'white' : '#666',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 600,
      fontSize: '16px',
      flexShrink: 0
    }),
    stepContent: { flex: 1, minWidth: 0 },
    stepTitle: { fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' },
    stepDesc: { fontSize: '14px', color: '#666', lineHeight: 1.4 },
    stepAction: { flexShrink: 0 },
    rowButton: (variant: 'primary' | 'secondary' | 'success' | 'disabled') => ({
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: 600,
      border: 'none',
      borderRadius: '8px',
      cursor: variant === 'disabled' ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s',
      backgroundColor: variant === 'primary' ? '#007bff' : variant === 'success' ? '#28a745' : variant === 'secondary' ? '#17a2b8' : '#6c757d',
      color: 'white',
      opacity: variant === 'disabled' ? 0.6 : 1,
      whiteSpace: 'nowrap' as const
    }),
    formCard: { padding: '24px', border: '1px solid #e0e0e0', borderRadius: '12px', backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '12px' },
    button: (variant: 'primary' | 'secondary' | 'success' | 'disabled') => ({
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: 600,
      border: 'none',
      borderRadius: '8px',
      cursor: variant === 'disabled' ? 'not-allowed' : 'pointer',
      width: '100%',
      transition: 'all 0.2s',
      backgroundColor: variant === 'primary' ? '#007bff' : variant === 'success' ? '#28a745' : variant === 'secondary' ? '#17a2b8' : '#6c757d',
      color: 'white',
      opacity: variant === 'disabled' ? 0.6 : 1
    }),
    form: { marginTop: '16px' },
    formGroup: { marginBottom: '16px' },
    label: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#333' },
    input: { width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', boxSizing: 'border-box' as const },
    tabGroup: { display: 'flex', gap: '8px', marginBottom: '16px' },
    tab: (isActive: boolean) => ({
      flex: 1,
      padding: '10px',
      border: isActive ? '2px solid #007bff' : '1px solid #ddd',
      borderRadius: '6px',
      backgroundColor: isActive ? '#e8f4fd' : 'white',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: isActive ? 600 : 400,
      color: isActive ? '#007bff' : '#666',
      textAlign: 'center' as const
    }),
    infoBox: { padding: '20px', border: '1px solid #e0e0e0', borderRadius: '12px', backgroundColor: '#f8f9fa' }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Welcome to Commerce Studio</h1>
        <p style={styles.subtitle}>AI-powered eyewear retail tools for your Shopify store</p>
        {shopDomain && <span style={styles.shopBadge}>🏪 {shopDomain}</span>}
      </div>

      {/* Success/Error Messages */}
      {successMessage && <div style={styles.successToast}>{successMessage}</div>}
      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* All Complete Banner */}
      {allStepsComplete && (
        <div style={styles.successBanner}>
          <strong style={{ fontSize: '18px' }}>🎉 Setup Complete!</strong>
          <p style={{ margin: '8px 0 0', color: '#155724' }}>
            Your Commerce Studio integration is ready. Access your dashboard to start using AI tools.
          </p>
        </div>
      )}

      {/* Steps - Row Layout */}
      <div style={styles.stepsContainer}>
        {/* Step 1: Connect Account */}
        <div style={styles.stepRow(isStepComplete('create-account'))}>
          <div style={styles.stepIndicator(isStepComplete('create-account'))}>
            {isStepComplete('create-account') ? '✓' : '1'}
          </div>
          <div style={styles.stepContent}>
            <div style={styles.stepTitle}>Connect Your Account</div>
            <div style={styles.stepDesc}>
              {isStepComplete('create-account')
                ? `Connected as ${userEmail}`
                : 'Create a new account or connect an existing Commerce Studio account'}
            </div>
          </div>
          <div style={styles.stepAction}>
            {!isStepComplete('create-account') && !showAccountForm && (
              <button onClick={() => setShowAccountForm(true)} style={styles.rowButton('primary')}>
                Get Started
              </button>
            )}
            {isStepComplete('create-account') && (
              <button style={styles.rowButton('disabled')} disabled>✓ Connected</button>
            )}
          </div>
        </div>

        {/* Account Form (expands below step 1 when clicked) */}
        {!isStepComplete('create-account') && showAccountForm && (
          <div style={styles.formCard}>
            <form onSubmit={handleCreateAccount}>
              <div style={styles.tabGroup}>
                <button type="button" onClick={() => setAccountMode('create')} style={styles.tab(accountMode === 'create')}>
                  New Account
                </button>
                <button type="button" onClick={() => setAccountMode('connect')} style={styles.tab(accountMode === 'connect')}>
                  Existing Account
                </button>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email Address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" style={styles.input} required />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={accountMode === 'create' ? 'Min 8 characters' : 'Your password'} style={styles.input} required minLength={8} />
              </div>

              {accountMode === 'create' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Confirm Password</label>
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm password" style={styles.input} required />
                </div>
              )}

              <button type="submit" disabled={isCreatingAccount} style={styles.button(isCreatingAccount ? 'disabled' : 'primary')}>
                {isCreatingAccount ? 'Processing...' : accountMode === 'create' ? 'Create Account' : 'Connect Account'}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Sync Products */}
        <div style={styles.stepRow(isStepComplete('sync-products'))}>
          <div style={styles.stepIndicator(isStepComplete('sync-products'))}>
            {isStepComplete('sync-products') ? '✓' : '2'}
          </div>
          <div style={styles.stepContent}>
            <div style={styles.stepTitle}>Sync Products</div>
            <div style={styles.stepDesc}>Import your Shopify product catalog for AI-powered recommendations</div>
          </div>
          <div style={styles.stepAction}>
            <button
              onClick={handleSyncProducts}
              disabled={!isStepComplete('create-account') || isSyncing || isStepComplete('sync-products')}
              style={styles.rowButton(
                isStepComplete('sync-products') ? 'disabled' : !isStepComplete('create-account') ? 'disabled' : isSyncing ? 'disabled' : 'success'
              )}
            >
              {isSyncing ? 'Syncing...' : isStepComplete('sync-products') ? '✓ Synced' : 'Sync Products'}
            </button>
          </div>
        </div>

        {/* Step 3: Open Commerce Studio Dashboard
            App configuration and activation lives in commerce.varai.ai.
            This step intentionally has no in-Shopify app picker — see the
            `allStepsComplete` comment above. */}
        <div style={styles.stepRow(allStepsComplete)}>
          <div style={styles.stepIndicator(allStepsComplete)}>
            {allStepsComplete ? '✓' : '3'}
          </div>
          <div style={styles.stepContent}>
            <div style={styles.stepTitle}>Configure & activate apps in Commerce Studio</div>
            <div style={styles.stepDesc}>
              All app configuration and activation lives in your Commerce Studio dashboard — open it to pick widgets, set styling, and turn them on for your store.
            </div>
          </div>
          <div style={styles.stepAction}>
            <button onClick={handleGoToDashboard} disabled={!allStepsComplete} style={styles.rowButton(allStepsComplete ? 'primary' : 'disabled')}>
              {allStepsComplete ? 'Open Dashboard →' : 'Complete Setup'}
            </button>
          </div>
        </div>
      </div>

      {/* What's Included — shown until the merchant has finished sync + opens the dashboard.
          Activation Guide / Auto-activation banners were removed: theme-block activation
          happens in Commerce Studio, not via Shopify theme editor. */}
      {!allStepsComplete && (
        <div style={styles.infoBox}>
          <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', color: '#1a1a2e' }}>
            Available Apps
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>👓 <strong>Virtual Try-On</strong> - AR eyewear preview</div>
            <div>📏 <strong>PD Measurement</strong> - Pupillary distance tool</div>
            <div>🤖 <strong>AI Size Recommender</strong> - Smart frame sizing</div>
            <div>🔍 <strong>Lens Journey</strong> - 8-step lens builder</div>
            <div>✨ <strong>Style Assistant</strong> - AI recommendations</div>
            <div>📊 <strong>Analytics Dashboard</strong> - Performance metrics</div>
            <div>🔎 <strong>Face Shape Analyzer</strong> - Face shape detection</div>
            <div>📐 <strong>Fitting Height</strong> - Medical-grade measurements</div>
            <div>🧊 <strong>3D Product Viewer</strong> - 360-degree product view</div>
            <div>💬 <strong>AI Chat Assistant</strong> - 24/7 customer support</div>
            <div>🔍 <strong>Intelligent Search</strong> - AI-powered search</div>
            <div>🏪 <strong>BOPIS</strong> - Buy online, pickup in-store</div>
          </div>
        </div>
      )}
    </div>
  );
}